import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@store/index';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required role(s) to access this route */
  roles?: string[];
}

/**
 * Wrapper component for protected routes
 * Redirects to login if user is not authenticated
 * Shows loading state while authentication is being checked
 */
function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuthStore();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
        role="status"
        aria-label={t('common.loading')}
      >
        <CircularProgress />
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role authorization if roles are specified
  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.includes(user.role);
    if (!hasRequiredRole) {
      // User is authenticated but not authorized
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User is authenticated (and authorized if roles were checked)
  return <>{children}</>;
}

export default ProtectedRoute;
